// pages/SectionsPage.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sectionService } from '../services/sectionService';
import { adminService } from '../services/adminService';
import JoinModal from '../components/Modals/JoinModal';
import './SectionsPage.css';

const SectionsPage = ({ 
  user = null, 
  addNotification = () => {},
  addAuditLog = () => {}
}) => {
  // Error boundary state
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Modal state
  const [selectedSection, setSelectedSection] = useState(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [memberData, setMemberData] = useState({
    fullName: user?.first_name + ' ' + user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    whatsApp: user?.whatsApp || '',
    age: '',
    joinDate: new Date().toISOString().split('T')[0],
    jerseyRequest: { need: false, size: '', type: 'borrow', quantity: 1 },
    equipmentRequests: [],
    notes: ''
  });

  // Sections state - fetched from SQL
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // User's existing requests state
  const [userRequests, setUserRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Jersey sizes from SQL
  const [jerseySizes, setJerseySizes] = useState(['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL']);

  // Group section equipment by category
  const equipmentByCategory = {};

  // Global error handler
  useEffect(() => {
    const errorHandler = (event) => {
      console.error('Global error caught:', event.error);
      if (event.error && event.error.message) {
        if (event.error.message.includes('Invalid or unexpected token') ||
            event.error.message.includes('Unexpected token')) {
          setHasError(true);
          setErrorMessage('There was an error rendering the sections. Please refresh the page.');
          event.preventDefault();
        }
      }
    };
    
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);

  // Fetch sections from SQL database using PUBLIC endpoint
  useEffect(() => {
    const fetchSectionsFromSQL = async () => {
      try {
        setLoading(true);
        console.log('📡 Fetching sections from SQL database...');
        
        // Use the public endpoint that doesn't require admin access
        const response = await sectionService.getAllSectionsPublic();
        
        console.log('📡 Sections API Response:', response);
        
        let sectionsData = [];
        if (response.success && response.sections) {
          sectionsData = response.sections;
        } else if (response.sections && Array.isArray(response.sections)) {
          sectionsData = response.sections;
        } else if (Array.isArray(response)) {
          sectionsData = response;
        }
        
        if (sectionsData && sectionsData.length > 0) {
          console.log(`✅ Loaded ${sectionsData.length} sections from SQL database`);
          setSections(sectionsData);
          setError(null);
        } else {
          console.warn('⚠️ No sections found in database');
          setError('No sections found in database');
          addNotification('No sections available', 'warning');
        }
      } catch (err) {
        console.error('❌ Error loading sections from SQL:', err);
        setError('Network error. Please try again later.');
        addNotification('Network error loading sections', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSectionsFromSQL();
  }, [addNotification]);

  // Fetch jersey sizes from SQL
  useEffect(() => {
    const fetchJerseySizes = async () => {
      try {
        const response = await sectionService.getJerseySizes();
        if (response.success && response.sizes && response.sizes.length > 0) {
          setJerseySizes(response.sizes);
          console.log('✅ Jersey sizes loaded from SQL:', response.sizes);
        }
      } catch (err) {
        console.error('Error fetching jersey sizes:', err);
        // Keep default sizes
      }
    };
    fetchJerseySizes();
  }, []);

  // Fetch user's existing section requests from SQL
  useEffect(() => {
    const fetchUserRequests = async () => {
      if (user?.id) {
        try {
          const response = await sectionService.getMyMemberships();
          console.log('📦 User Requests Response:', response);
          
          if (response.success && response.memberships) {
            console.log('📦 User Requests Details:', response.memberships.map(r => ({ 
              section_id: r.section_id, 
              status: r.status, 
              section_name: r.section_name 
            })));
            setUserRequests(response.memberships);
          }
        } catch (err) {
          console.error('Error fetching user requests:', err);
        }
      }
    };
    fetchUserRequests();
  }, [user?.id]);

  // Log sections for debugging
  useEffect(() => {
    if (sections.length > 0) {
      console.log('📦 Sections loaded from SQL:', sections.length);
    }
  }, [sections]);

  // Get unique categories from SQL data
  const categories = ['all', ...new Set(sections.map(s => s?.category).filter(Boolean))];

  // Filter sections
  const filteredSections = sections.filter(section => {
    const matchesCategory = selectedCategory === 'all' || section?.category === selectedCategory;
    const matchesSearch = section?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         section?.instructor?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleJoinSection = (section) => {
    const sectionId = Number(section.id);
    const existingRequest = userRequests.find(req => Number(req.section_id) === sectionId);
    
    console.log(`🔍 Checking section ${sectionId} (${section.name}) - Existing request:`, existingRequest);
    
    if (existingRequest) {
      let statusMessage = '';
      let statusType = 'warning';
      
      if (existingRequest.status === 'pending') {
        statusMessage = 'You already have a pending request for this section. Please wait for approval.';
      } else if (existingRequest.status === 'approved') {
        statusMessage = 'You are already a member of this section!';
      } else if (existingRequest.status === 'rejected') {
        statusMessage = 'Your previous request was rejected. You can apply again.';
        statusType = 'info';
      } else {
        statusMessage = 'You have already requested this section.';
      }
      
      addNotification(statusMessage, statusType);
      
      if (existingRequest.status !== 'rejected') {
        return;
      }
    }
    
    setSelectedSection(section);
    setMemberData({
      ...memberData,
      fullName: user?.first_name + ' ' + user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      whatsApp: user?.whatsApp || '',
      equipmentRequests: []
    });
    setShowJoinModal(true);
  };

  const handleJoinSubmit = async () => {
    if (!selectedSection) return;
    
    setSubmitting(true);

    const requestData = {
      section_id: selectedSection.id,
      jersey_request: memberData.jerseyRequest.need ? memberData.jerseyRequest : null,
      equipment_requests: memberData.equipmentRequests,
      notes: memberData.notes
    };

    try {
      const response = await sectionService.joinSection(requestData);
      console.log('📡 Join response:', response);
      
      if (response.success) {
        addNotification(`Your request for ${selectedSection.name} has been submitted for approval.`, 'success');
        if (addAuditLog) {
          addAuditLog('Section membership request submitted', {
            section: selectedSection.name,
            user: user?.email
          });
        }
        
        // Refresh user requests
        const updatedRequests = await sectionService.getMyMemberships();
        if (updatedRequests.success && updatedRequests.memberships) {
          setUserRequests(updatedRequests.memberships);
        }
        
        setShowJoinModal(false);
      } else {
        const errorMessage = response.message || 'Failed to submit request';
        addNotification(errorMessage, 'error');
        
        if (errorMessage.includes('already requested')) {
          addNotification('You already have a pending or approved request for this section. Please wait for approval.', 'warning');
          
          const updatedRequests = await sectionService.getMyMemberships();
          if (updatedRequests.success && updatedRequests.memberships) {
            setUserRequests(updatedRequests.memberships);
          }
        }
      }
    } catch (err) {
      console.error('Join error:', err);
      addNotification('An error occurred. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleJerseyRequest = () => {
    setMemberData({
      ...memberData,
      jerseyRequest: {
        ...memberData.jerseyRequest,
        need: !memberData.jerseyRequest.need
      }
    });
  };

  const toggleEquipmentRequest = (equipment) => {
    const exists = memberData.equipmentRequests.find(e => e.id === equipment.id);
    if (exists) {
      setMemberData({
        ...memberData,
        equipmentRequests: memberData.equipmentRequests.filter(e => e.id !== equipment.id)
      });
    } else {
      setMemberData({
        ...memberData,
        equipmentRequests: [...memberData.equipmentRequests, { 
          id: equipment.id,
          name: equipment.name,
          category: equipment.category,
          icon: equipment.icon,
          price: equipment.price || 0,
          quantity: 1,
          size: equipment.sizes ? equipment.sizes[0] : '',
          type: 'borrow',
          available: equipment.available
        }]
      });
    }
  };

  const updateEquipmentRequest = (id, field, value) => {
    setMemberData({
      ...memberData,
      equipmentRequests: memberData.equipmentRequests.map(e =>
        e.id === id ? { ...e, [field]: value } : e
      )
    });
  };

  const removeEquipmentRequest = (id) => {
    setMemberData({
      ...memberData,
      equipmentRequests: memberData.equipmentRequests.filter(e => e.id !== id)
    });
  };

  // Get button text and status for a section
  const getButtonStatus = (section) => {
    const sectionId = Number(section.id);
    const request = userRequests.find(req => Number(req.section_id) === sectionId);
    
    if (request) {
      if (request.status === 'pending') {
        return { text: 'Pending Approval', icon: 'fa-clock', disabled: true, className: 'pending' };
      }
      if (request.status === 'approved') {
        return { text: 'Member', icon: 'fa-check-circle', disabled: true, className: 'approved' };
      }
      if (request.status === 'rejected') {
        const spotsLeft = (section.capacity || 0) - (section.enrolled || 0);
        if (spotsLeft <= 0) {
          return { text: 'Join Waitlist', icon: 'fa-clock', disabled: false, className: 'waitlist' };
        }
        return { text: 'Apply Again', icon: 'fa-user-plus', disabled: false, className: 'rejected' };
      }
    }
    
    const spotsLeft = (section.capacity || 0) - (section.enrolled || 0);
    if (spotsLeft <= 0) {
      return { text: 'Join Waitlist', icon: 'fa-clock', disabled: false, className: 'waitlist' };
    }
    
    return { text: 'Join Section', icon: 'fa-user-plus', disabled: false, className: '' };
  };

  // Clean string function to remove hidden characters
  const cleanString = (str) => {
    if (!str) return '';
    return str.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
  };

  if (hasError) {
    return (
      <div className="sections-page error-container">
        <i className="fa-solid fa-circle-exclamation"></i>
        <h3>Something went wrong</h3>
        <p>{errorMessage}</p>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sections-page loading">
        <div className="spinner"></div>
        <p>Loading sections from database...</p>
      </div>
    );
  }

  if (error && sections.length === 0) {
    return (
      <div className="sections-page error">
        <i className="fa-solid fa-circle-exclamation"></i>
        <h3>Unable to load sections</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="sections-page">
      <div className="page-header">
        <h2 className="page-title">
          <i className="fa-solid fa-layer-group"></i>
          Club Sections & Activities ({sections.length})
        </h2>
      </div>
      
      <div className="search-bar">
        <i className="fa-solid fa-search"></i>
        <input
          type="text"
          placeholder="Search sections or instructors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="category-filters">
        {categories.map(cat => (
          <button
            key={cat}
            className={`category-filter ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat === 'all' ? 'All' : cat}
          </button>
        ))}
      </div>

      <div className="sections-grid">
        {filteredSections.length > 0 ? (
          filteredSections.map(section => {
            try {
              const totalEnrolled = parseInt(section.enrolled) || 0;
              const capacity = parseInt(section.capacity) || 0;
              const spotsLeft = capacity - totalEnrolled;
              const enrolledPercentage = capacity > 0 ? (totalEnrolled / capacity) * 100 : 0;
              const buttonStatus = getButtonStatus(section);

              return (
                <motion.div
                  key={section.id}
                  className="section-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="card-image">
                    <img 
                      src={section.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23667eea"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="16"%3ENo Image%3C/text%3E%3C/svg%3E'} 
                      alt={cleanString(section.name)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23667eea"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="16"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <div className="card-category">
                      <i className={section.icon || 'fa-solid fa-users'}></i>
                      {cleanString(section.category)}
                    </div>
                    {spotsLeft < 5 && spotsLeft > 0 && (
                      <div className="badge spots-left">
                        <i className="fa-solid fa-exclamation-circle"></i>
                        Only {spotsLeft} left!
                      </div>
                    )}
                    {spotsLeft === 0 && (
                      <div className="badge waitlist-badge">
                        <i className="fa-solid fa-clock"></i>
                        Waitlist Available
                      </div>
                    )}
                    {buttonStatus.disabled && buttonStatus.text === 'Member' && (
                      <div className="badge member-badge">
                        <i className="fa-solid fa-check-circle"></i>
                        Member
                      </div>
                    )}
                    {buttonStatus.text === 'Pending Approval' && (
                      <div className="badge pending-badge">
                        <i className="fa-solid fa-clock"></i>
                        Pending
                      </div>
                    )}
                  </div>

                  <div className="card-content">
                    <h3 className="card-title">{cleanString(section.name)}</h3>
                    
                    <div className="instructor-info">
                      <div className="instructor-avatar">
                        <i className="fa-solid fa-user-tie"></i>
                      </div>
                      <div className="instructor-details">
                        <span className="instructor-name">{cleanString(section.instructor)}</span>
                        <span className="instructor-bio">{cleanString(section.bio?.substring(0, 60))}...</span>
                      </div>
                    </div>

                    <div className="section-details">
                      <div className="detail">
                        <i className="fa-regular fa-calendar detail-icon"></i>
                        <span>{cleanString(section.schedule)}</span>
                      </div>
                      <div className="detail">
                        <i className="fa-regular fa-clock detail-icon"></i>
                        <span>{cleanString(section.duration)}</span>
                      </div>
                      <div className="detail">
                        <i className="fa-solid fa-location-dot detail-icon"></i>
                        <span>{cleanString(section.location)}</span>
                      </div>
                      <div className="detail">
                        <i className="fa-solid fa-users detail-icon"></i>
                        <span>{cleanString(section.age_group)}</span>
                      </div>
                    </div>

                    <div className="equipment-required">
                      <h4>Required Equipment:</h4>
                      <div className="equipment-tags">
                        {section.equipment && Object.keys(section.equipment).slice(0, 4).map(key => (
                          <span key={key} className="equipment-tag">
                            <i className="fa-solid fa-circle"></i>
                            {cleanString(key)}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="availability-bar">
                      <div className="availability-label">
                        <span>
                          <i className="fa-solid fa-users"></i> Members
                        </span>
                        <span className={spotsLeft < 5 ? 'text-warning' : ''}>
                          {totalEnrolled}/{capacity}
                        </span>
                      </div>
                      {capacity > 0 && (
                        <>
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${Math.min(enrolledPercentage, 100)}%` }}
                            ></div>
                          </div>
                          {spotsLeft > 0 ? (
                            <span className="spots-text">{spotsLeft} spots available</span>
                          ) : (
                            <span className="spots-text full">Section full</span>
                          )}
                        </>
                      )}
                    </div>

                    <div className="activities-preview">
                      <h4>Activities:</h4>
                      <div className="activities-list">
                        {section.activities && section.activities.slice(0, 2).map(activity => (
                          <div key={activity.id} className="activity-item">
                            <i className="fa-regular fa-clock"></i>
                            <span>{cleanString(activity.name)}</span>
                            <small>{cleanString(activity.time)}</small>
                          </div>
                        ))}
                        {section.activities && section.activities.length > 2 && (
                          <span className="more-activities">
                            +{section.activities.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      className={`join-btn ${buttonStatus.className} ${buttonStatus.disabled ? 'disabled' : ''}`}
                      onClick={() => handleJoinSection(section)}
                      disabled={buttonStatus.disabled || submitting}
                    >
                      <i className={`fa-solid ${buttonStatus.icon}`}></i>
                      {buttonStatus.text}
                    </button>
                  </div>
                </motion.div>
              );
            } catch (err) {
              console.error('Error rendering section:', section, err);
              return null;
            }
          })
        ) : (
          <div className="no-results">
            <i className="fa-solid fa-search"></i>
            <h3>No sections found</h3>
            <p>Try adjusting your search or filter</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showJoinModal && selectedSection && (
          <JoinModal
            section={selectedSection}
            user={user}
            memberData={memberData}
            setMemberData={setMemberData}
            onClose={() => setShowJoinModal(false)}
            onSubmit={handleJoinSubmit}
            jerseySizes={jerseySizes}
            equipmentByCategory={equipmentByCategory}
            toggleJerseyRequest={toggleJerseyRequest}
            toggleEquipmentRequest={toggleEquipmentRequest}
            updateEquipmentRequest={updateEquipmentRequest}
            removeEquipmentRequest={removeEquipmentRequest}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SectionsPage;